// fields that are allowed for all BATs
const coreFields: Array<string> = ['month'];

// fields that are allowed for all BATs that have location data
const locationFields: Array<string> = [
  'location_country',
  'location_subdivision',
  'location_city',
  'location_grid',
  'location_postal_code',
];

// fields that are allowed for all BATs that have vendor data
const vendorFields: Array<string> = ['vendor_entity'];

// fields that are allowed for all BATs that have building data
const buildingFields: Array<string> = [
  'building_display_name',
  'building_lease_type',
  'building_kind',
];

// fields that are allowed for all BATs that have product data
const productFields: Array<string> = ['product', 'product_id'];

// fields that are allowed for all BATs that have material data
const materialFields: Array<string> = [
  'purchased_material_id',
  'purchased_material_name',
];

// fields that are allowed for all BATs that have waste data
const wasteFields: Array<string> = [
  'is_waste_hazardous',
  'is_waste_radioactive',
  'waste_lifecycle',
  'waste_material',
  'waste_treatment',
];

/**
 * extBART columns that are allowed to be used for customization May or may not be
 * actually available to a calculation model when generating a footprint hence
 * we must use them.
 *
 * This list is snapshotted and will need to be updated when
 * new columns are added or removed from the allow list
 */
const allowedBatColumns: ReadonlyMap<string, ReadonlySet<string>> = new Map([
  [
    'electricity_consumption',
    new Set([
      ...coreFields,
      ...buildingFields,
      ...locationFields,
      ...vendorFields,
      ...productFields,
      ...materialFields,
      'clean_power_percent',
      'clean_power_contractual_instrument',
      'onsite_clean_power_percent',
    ]),
  ],
  [
    'electricity_consumption_datacenter',
    new Set([
      ...coreFields,
      ...buildingFields,
      ...locationFields,
      ...vendorFields,
      ...productFields,
      ...materialFields,
      'clean_power_percent',
      'clean_power_contractual_instrument',
    ]),
  ],
  [
    'non_building_electricity_consumption',
    new Set([
      ...coreFields,
      ...locationFields,
      ...vendorFields,
      ...productFields,
      ...materialFields,
      'clean_power_percent',
      'clean_power_contractual_instrument',
      'electricity_consumption_reason',
      'is_third_party',
    ]),
  ],
  [
    'employees',
    new Set([
      ...coreFields,
      ...locationFields,
      'employee_full_time_equivalent',
      'employee_remote_time_percent',
      'commute_bike_percent',
      'commute_car_percent',
      'commute_transit_percent',
      'commute_walk_percent',
    ]),
  ],
  [
    'building_occupation',
    new Set([
      ...coreFields,
      ...buildingFields,
      ...locationFields,
      ...vendorFields,
      'clean_power_percent',
      'clean_power_contractual_instrument',
      'onsite_clean_power_percent',
      'is_building_empty',
    ]),
  ],
  [
    'building_stationary_combustion',
    new Set([
      ...coreFields,
      ...buildingFields,
      ...locationFields,
      ...vendorFields,
      ...productFields,
      'fuel_kind',
      'fuel_combustion_reason',
    ]),
  ],
  [
    'expenditure_mapped',
    new Set([
      ...coreFields,
      ...locationFields,
      ...vendorFields,
      ...productFields,
      ...materialFields,
      'financial_account_id',
      'financial_account_name',
      'bea_code',
    ]),
  ],
  [
    'fuel_combustion',
    new Set([
      ...coreFields,
      ...locationFields,
      ...vendorFields,
      ...productFields,
      'fuel_kind',
      'fuel_combustion_reason',
      'is_third_party',
      'vehicle_type',
    ]),
  ],
  [
    'logistics',
    new Set([
      ...coreFields,
      ...vendorFields,
      ...productFields,
      ...materialFields,
      'destination_location_city',
      'destination_location_country',
      'destination_location_postal_code',
      'destination_location_subdivision',
      'distance_method',
      'exclude_first_mile',
      'exclude_last_mile',
      'logistics_position',
      'number_of_trips',
      'origin_location_city',
      'origin_location_country',
      'origin_location_postal_code',
      'origin_location_subdivision',
      'transportation_mode',
    ]),
  ],
  [
    'refrigerants',
    new Set([
      ...coreFields,
      ...buildingFields,
      ...locationFields,
      ...productFields,
      'refrigerant_leakage_source',
      'refrigerant_name',
    ]),
  ],
  [
    'sold_refrigerant',
    new Set([
      ...coreFields,
      ...locationFields,
      ...productFields,
      ...vendorFields,
      'refrigerant_name',
    ]),
  ],
  [
    'waste',
    new Set([
      ...coreFields,
      ...locationFields,
      ...productFields,
      ...vendorFields,
      ...wasteFields,
      'building_display_name',
    ]),
  ],
  [
    'building_waste',
    new Set([
      ...coreFields,
      ...buildingFields,
      ...locationFields,
      ...vendorFields,
      ...productFields,
      ...wasteFields,
    ]),
  ],
  [
    'waste_products',
    new Set([
      ...coreFields,
      ...locationFields,
      ...vendorFields,
      ...productFields,
      ...wasteFields,
      'products_sold_quantity',
    ]),
  ],
  [
    'direct_ghg_emissions',
    new Set([
      ...coreFields,
      ...buildingFields,
      ...locationFields,
      ...vendorFields,
      ...productFields,
      ...materialFields,
      'ghg_emission_reason',
      'gas_name',
      'ghg_category_id',
    ]),
  ],
  [
    'sold_vehicle',
    new Set([
      ...coreFields,
      ...vendorFields,
      ...locationFields,
      ...productFields,
      'vehicle_type',
      'fuel_consumed_time_of_sale',
      'fuel_kind',
      'vehicle_quantity',
    ]),
  ],
  [
    'vehicles',
    new Set([
      ...coreFields,
      ...vendorFields,
      ...locationFields,
      ...productFields,
      ...materialFields,
      'is_company_owned_or_leased',
      'vehicle_type',
      'vehicle_usage_reason',
    ]),
  ],
  [
    'travel_air',
    new Set([
      ...coreFields,
      ...vendorFields,
      'trip_kind',
      'passenger_quantity',
      'flight_cabin_class',
      'flight_route',
      'exclude_radiative_forcing',
    ]),
  ],
  [
    'travel_private_jets',
    new Set([
      ...coreFields,
      ...vendorFields,
      'trip_kind',
      'flight_route',
      'is_company_owned_or_leased',
      'private_plane_model',
    ]),
  ],
  [
    'travel_rail',
    new Set([
      ...coreFields,
      ...vendorFields,
      ...locationFields,
      'passenger_quantity',
      'transportation_mode',
      'trip_kind',
    ]),
  ],
  [
    'carbon_credit_retirements',
    new Set([
      ...coreFields,
      ...vendorFields,
      'contract_id',
      'retirement_date',
      'location_country',
      'carbon_removal_type',
      'is_3rd_party_verified',
      'quality_standard',
      'qualifies_as_corresponding_adjustment',
      'quantity_contracted_kgco2e',
    ]),
  ],
  [
    'carbon_removal',
    new Set([
      ...coreFields,
      'vendor_entity',
      'carbon_removal_kind',
      'kgco2e_removed',
    ]),
  ],
  [
    'cloud_compute_usage',
    new Set([
      ...coreFields,
      ...vendorFields,
      ...locationFields,
      'cloud_service',
      'cloud_region',
      'cloud_vm_instance_type',
    ]),
  ],
  [
    'cloud_spend',
    new Set([
      ...coreFields,
      ...vendorFields,
      'cloud_service',
      'cloud_vm_instance_type',
      'vendor_entity',
    ]),
  ],
  [
    'corporate_315_investments',
    new Set([
      ...coreFields,
      ...locationFields,
      'bea_code',
      // This is unfortunate... It gets mapped to asset name in drilldown
      // and is uploaded by the user as asset name but the BART column is ws_description.
      'ws_description',
      'asset_value',
      'outstanding_amount',
    ]),
  ],
  [
    'data_transfer',
    new Set([
      ...coreFields,
      ...locationFields,
      ...vendorFields,
      'network_type',
      'use_case',
      'description',
    ]),
  ],
  [
    'lifetime_fuel_and_electricity_use_of_sold_products',
    new Set([
      ...coreFields,
      ...locationFields,
      ...vendorFields,
      ...productFields,
      'product_category',
      'fuel_kind',
    ]),
  ],
  [
    'lodging',
    new Set([
      ...coreFields,
      ...locationFields,
      ...vendorFields,
      'lodging_unit_quantity',
    ]),
  ],
  [
    'sold_retail_good',
    new Set([
      ...coreFields,
      ...locationFields,
      ...vendorFields,
      ...productFields,
      'product_category',
      'is_energy_star',
    ]),
  ],
  [
    'water_usage',
    new Set([
      ...coreFields,
      ...buildingFields,
      ...locationFields,
      ...vendorFields,
      ...productFields,
    ]),
  ],
]);

/**
 * Check if a BAT name is allowed to be used for customization.
 */
export const isAllowedBatName = (batName: string): boolean => {
  return allowedBatColumns.has(batName);
};

/**
 * Check if a column for a given BAT name is allowed to be used for customization.
 *
 * If the BAT name is not in the allowedBatColumns map, return false.
 */
export const isAllowedBatColumn = (
  batName: string,
  column: string
): boolean => {
  return allowedBatColumns.get(batName)?.has(column) ?? false;
};

export const exportedForTesting = {
  allowedBatColumns,
};
